import { useState, useEffect } from 'react';

const DEFAULT_SETTINGS = {
  // Configuraciones de precios
  defaultShowPricesWithTax: true,
  autoSelectPriceDisplayByClientType: true,
  professionalClientsShowWithoutTax: true,
  
  // Configuraciones de descuentos
  enableQuickDiscounts: true,
  quickDiscountValues: [5, 10, 15],
  quickIncrementValues: [10],
  maxGlobalDiscountPercent: 50,
  maxGlobalDiscountAmount: 1000,
  
  // Configuraciones de comportamiento
  autoAddFirstCalculation: true,
  showItemDiscountInDescription: true,
  requireConfirmationForLargeDiscounts: true,
  largeDiscountThreshold: 25,
  
  // Configuraciones de interfaz
  showTaxIncludedToggle: true,
  showGlobalDiscountSection: true,
  numpadDecimalPlaces: 2,
  currencySymbol: '€',
  
  // Configuraciones de caja
  defaultPaymentMethod: 'cash',
  suggestChangeAmounts: true,
  quickCashAmounts: [5, 10, 20, 50, 100],
  
  // Configuraciones de tickets
  showItemCodes: false,
  showDetailedTaxBreakdown: true,
  printTicketAutomatically: false
};

const STORAGE_KEY = 'iltzar_tpv_settings';

export const useTpvSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  // Cargar configuraciones al inicializar
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings({ ...DEFAULT_SETTINGS, ...parsedSettings });
      }
    } catch (error) {
      console.error('Error loading TPV settings:', error);
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = (newSettings) => {
    try {
      const settingsToSave = { ...DEFAULT_SETTINGS, ...newSettings };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsToSave));
      setSettings(settingsToSave);
      return true;
    } catch (error) {
      console.error('Error saving TPV settings:', error);
      return false;
    }
  };

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    return saveSettings(newSettings);
  };

  const resetSettings = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setSettings(DEFAULT_SETTINGS);
      return true;
    } catch (error) {
      console.error('Error resetting TPV settings:', error);
      return false;
    }
  };

  // Funciones auxiliares para determinar configuraciones dinámicas
  const shouldShowPricesWithTax = (clientType = null) => {
    if (settings.autoSelectPriceDisplayByClientType && clientType) {
      // Si el cliente es profesional y está configurado para mostrar sin IVA
      if (clientType === 'professional' && settings.professionalClientsShowWithoutTax) {
        return false;
      }
      // Para clientes particulares, mostrar con IVA
      if (clientType === 'particular') {
        return true;
      }
    }
    // Usar la configuración por defecto
    return settings.defaultShowPricesWithTax;
  };

  const getActiveQuickDiscounts = () => {
    if (!settings.enableQuickDiscounts) return [];
    
    const discounts = settings.quickDiscountValues
      .filter(value => value > 0)
      .map(value => ({ type: 'discount', value: -value, label: `-${value}%` }));
    
    const increments = settings.quickIncrementValues
      .filter(value => value > 0)
      .map(value => ({ type: 'increment', value: value, label: `+${value}%` }));
    
    return [...discounts, ...increments];
  };

  const getActiveQuickCashAmounts = () => {
    return settings.quickCashAmounts.filter(amount => amount > 0).sort((a, b) => a - b);
  };

  const shouldRequireDiscountConfirmation = (discountPercent) => {
    return settings.requireConfirmationForLargeDiscounts && 
           Math.abs(discountPercent) >= settings.largeDiscountThreshold;
  };

  const getMaxAllowedDiscount = (isPercent) => {
    return isPercent ? settings.maxGlobalDiscountPercent : settings.maxGlobalDiscountAmount;
  };

  return {
    settings,
    isLoading,
    saveSettings,
    updateSetting,
    resetSettings,
    
    // Funciones auxiliares
    shouldShowPricesWithTax,
    getActiveQuickDiscounts,
    getActiveQuickCashAmounts,
    shouldRequireDiscountConfirmation,
    getMaxAllowedDiscount,
    
    // Acceso directo a configuraciones comunes
    defaultPaymentMethod: settings.defaultPaymentMethod,
    autoAddFirstCalculation: settings.autoAddFirstCalculation,
    showTaxIncludedToggle: settings.showTaxIncludedToggle,
    showGlobalDiscountSection: settings.showGlobalDiscountSection,
    currencySymbol: settings.currencySymbol
  };
};